import { useCombobox } from "@mantine/core";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import { VisualState } from "~/components/dashboard/PageTable/filters/SelectFilter";
import { useMediaQuery } from "~/hooks/useMediaQuery";

import { PageBulkRecrawlAttributes } from "../../../api/Api";
import { Icon } from "../../../assets/icons/Icon";
import { DEFAULT_STATUSES } from "../../../pages/dashboard/PagesPage";
import Button from "../../mantine/button/Button";
import { PaginationConfig } from "../../ui/pagination/Pagination";
import { SearchBar } from "../../ui/search/SearchBar";
import {
  AdvancedConditionsModal,
  type ConditionGroup,
} from "./AdvancedConditionsModal";
import styles from "./PageFilters.module.scss";
import RecrawlCombobox from "./RecrawlCombobox";
import SelectFilter from "./filters/SelectFilter";
import selectFilterStyles from "./filters/SelectFilter.module.scss";
import SelectFilterButton from "./filters/SelectFilterButton";
import {
  ACTIVE_STATUSES,
  areEqualAsSets,
  formatLabel,
  getItemClass,
  statusObjects,
} from "./statusConstants";

const FILTER_VALUES = {
  ACTIVE: "active",
  ALL: "all",
  DIVIDER: "divider",
  NONE: "none",
};

const FILTER_TYPES = {
  STATUS: "status" as const,
  TYPE: "type" as const,
  CUSTOMIZATION: "customization" as const,
};

const FILTER_LABELS = {
  ACTIVE_STATUS: "Status: Active",
  ANY_STATUS: "Status: Any",
  ANY_TYPE: "Type: Any",
  ANY_CUSTOMIZATION: "All Pages",
  NO_CUSTOMIZATION: "No AI Content",
  STATUS: "Status",
  TYPE: "Type",
  CUSTOMIZATION: "AI Content",
};

const CUSTOMIZATION_OPTIONS: { value: string; label: string }[] = [
  { value: "title", label: "Meta Title" },
  { value: "description", label: "Meta Description" },
  { value: "schema", label: "JSON-LD Schema" },
];

const AVAILABLE_CUSTOMIZATIONS = CUSTOMIZATION_OPTIONS.map((o) => o.value);

const getStatusFilterTitle = (selectedStatuses: string[]): string => {
  const isActiveSelected = areEqualAsSets(selectedStatuses, ACTIVE_STATUSES);

  if (isActiveSelected) {
    return FILTER_LABELS.ACTIVE_STATUS;
  }

  if (selectedStatuses.includes(FILTER_VALUES.ALL)) {
    return FILTER_LABELS.ANY_STATUS;
  }

  const firstValue = selectedStatuses[0];

  const firstStatus = statusObjects.find((obj) => obj.value === firstValue);

  const firstLabel = firstStatus?.label ?? formatLabel(firstValue);

  if (selectedStatuses.length === 1) {
    return `Status: ${firstLabel}`;
  }

  return `Status: ${firstLabel} & ${selectedStatuses.length - 1} more`;
};

const getTypeFilterTitle = (selectedPageTypes: string[]): string => {
  if (selectedPageTypes.includes(FILTER_VALUES.ALL)) {
    return FILTER_LABELS.ANY_TYPE;
  }

  const firstValue = selectedPageTypes[0];
  const firstLabel = formatLabel(firstValue);

  if (selectedPageTypes.length === 1) {
    return `Type: ${firstLabel}`;
  }

  return `Type: ${firstLabel} & ${selectedPageTypes.length - 1} more`;
};

const getCustomizationFilterTitle = (
  selectedCustomizations: string[],
): string => {
  if (selectedCustomizations.includes(FILTER_VALUES.ALL)) {
    return "AI Content: All";
  }

  if (selectedCustomizations.includes(FILTER_VALUES.NONE)) {
    return "AI Content: None";
  }

  const firstValue = selectedCustomizations[0];
  const firstLabel =
    CUSTOMIZATION_OPTIONS.find((o) => o.value === firstValue)?.label ??
    formatLabel(firstValue);

  if (selectedCustomizations.length === 1) {
    return `AI Content: ${firstLabel}`;
  }

  return `AI Content: ${firstLabel} & ${
    selectedCustomizations.length - 1
  } more`;
};

const getStatusCheckboxVisualState = (
  value: string,
  selectedStatuses: string[],
  hoveredValue: string | null,
): VisualState => {
  const isActiveSelected = areEqualAsSets(selectedStatuses, ACTIVE_STATUSES);
  const isAllSelected = selectedStatuses.includes(FILTER_VALUES.ALL);

  // ACTIVE preset
  if (value === FILTER_VALUES.ACTIVE) {
    return isActiveSelected ? "checked" : "none";
  }

  // ANY preset
  if (value === FILTER_VALUES.ALL) {
    return isAllSelected ? "checked" : "none";
  }

  // if hovered + crossed → checked
  if (hoveredValue === value) {
    return "checked";
  }

  // Cross logic for ACTIVE preset
  if (isActiveSelected && ACTIVE_STATUSES.includes(value)) {
    return "crossed";
  }

  // Cross logic for ANY preset
  if (isAllSelected) {
    return "crossed";
  }

  return selectedStatuses.includes(value) ? "checked" : "none";
};

const getTypeCheckboxVisualState = (
  value: string,
  selectedPageTypes: string[],
  hoveredValue: string | null,
): VisualState => {
  const isAllSelected = selectedPageTypes.includes(FILTER_VALUES.ALL);

  // preset row
  if (value === FILTER_VALUES.ALL) {
    return isAllSelected ? "checked" : "none";
  }

  // hover → checked
  if (hoveredValue === value) {
    return "checked";
  }

  if (isAllSelected) {
    return "crossed";
  }

  return selectedPageTypes.includes(value) ? "checked" : "none";
};

const getCustomizationCheckboxVisualState = (
  value: string,
  selectedCustomizations: string[],
  hoveredValue: string | null,
): VisualState => {
  const isAllSelected = selectedCustomizations.includes(FILTER_VALUES.ALL);
  const isNoneSelected = selectedCustomizations.includes(FILTER_VALUES.NONE);

  if (value === FILTER_VALUES.ALL) {
    return isAllSelected ? "checked" : "none";
  }

  // "No AI Content" is conceptually inside the "All Pages" preset:
  // when All is on, show it as crossed/subsumed too — so users don't read
  // "All" as "everything except pages without customizations".
  if (value === FILTER_VALUES.NONE) {
    if (isNoneSelected) return "checked";
    if (isAllSelected) return "crossed";

    return "none";
  }

  if (hoveredValue === value) {
    return "checked";
  }

  if (isAllSelected) {
    return "crossed";
  }

  if (isNoneSelected) {
    return "none";
  }

  return selectedCustomizations.includes(value) ? "checked" : "none";
};

type FilterType =
  | typeof FILTER_TYPES.STATUS
  | typeof FILTER_TYPES.TYPE
  | typeof FILTER_TYPES.CUSTOMIZATION;

interface PageFiltersProps {
  searchValue?: string;
  setSearchValue?: (value: string) => void;
  selectedStatuses: string[];
  setSelectedStatuses: (
    statuses: string[] | ((prev: string[]) => string[]),
  ) => void;
  selectedIds?: number[];
  selectedIdsToRecrawl?: number[];
  selectedIdsToIgnore?: number[];
  hasMixedIgnoreStatuses?: boolean;
  canSoftCrawl?: boolean;
  canForceRecrawl?: boolean;
  canAbortCrawl?: boolean;
  recrawlPagesBulk?: (ids: PageBulkRecrawlAttributes) => void;
  openBulkIgnoreModal?: () => void;
  openCrawlModal?: (type: "soft" | "force") => void;
  setSelectedIds?: (ids: number[]) => void;
  combobox?: ReturnType<typeof useCombobox>;
  isOpened?: boolean;
  setIsOpened?: (isOpened: boolean) => void;
  openAddUrlModal?: () => void;
  isCompactView?: boolean;
  setIsCompactView?: (isCompact: boolean) => void;
  canAddUrl?: boolean;
  canGenerate?: boolean;
  onGenerateClick?: (selectedIds: number[]) => void;
  advancedConditions?: ConditionGroup[];
  onAdvancedConditionsChange?: (groups: ConditionGroup[]) => void;
  selectedPageTypes?: string[];
  setSelectedPageTypes?: (
    types: string[] | ((prev: string[]) => string[]),
  ) => void;
  selectedCustomizations?: string[];
  setSelectedCustomizations?: (
    values: string[] | ((prev: string[]) => string[]),
  ) => void;
  pageTypesDetail: Record<string, number>;
  pageStatusesDetail: Record<string, number>;
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
  isLoading?: boolean;
}

/**
 * TODO (refactor if any other preset or <SelectFilter> will appear):
 *
 * Status and Type filters now share almost identical logic:
 * - preset handling (ALL / ACTIVE)
 * - collapse to ALL when all available options selected
 * - hover → checked override
 * - visual state calculation
 *
 * If filter complexity grows, consider extracting a generic
 * "multi-select with preset" helper:
 *
 *   createPresetMultiSelect({
 *     presetValues,
 *     getAvailableValues,
 *   })
 *
 * This would remove duplication between Status and Type
 * and isolate preset/collapse logic from UI concerns.
 *
 * Current implementation is intentionally explicit
 * for readability and easier debugging.
 */

const PageFilters = ({
  isLoading,
  searchValue = "",
  setSearchValue = () => {},
  selectedStatuses,
  setSelectedStatuses,
  selectedIds = [],
  selectedIdsToRecrawl = [],
  selectedIdsToIgnore = [],
  hasMixedIgnoreStatuses = false,
  canSoftCrawl = false,
  canForceRecrawl = false,
  canAbortCrawl = false,
  recrawlPagesBulk = () => {},
  openBulkIgnoreModal = () => {},
  openCrawlModal = () => {},
  setSelectedIds = () => {},
  combobox,
  isOpened = false,
  setIsOpened = () => {},
  openAddUrlModal = () => {},
  isCompactView = false,
  setIsCompactView = () => {},
  canAddUrl = false,
  canGenerate = false,
  onGenerateClick = () => {},
  advancedConditions = [],
  onAdvancedConditionsChange = () => {},
  selectedPageTypes = [],
  setSelectedPageTypes = () => {},
  selectedCustomizations = [FILTER_VALUES.ALL],
  setSelectedCustomizations = () => {},
  pageTypesDetail,
  pageStatusesDetail,
  setPagination,
  pagination,
}: PageFiltersProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState<string>(searchValue);
  // Matches the breakpoint in PageFilters.module.scss that hides button
  // labels. Tooltips are only useful in the compact state where buttons
  // show just an icon without text.
  const isCompact = useMediaQuery(`(max-width: ${styles.compactBreakpoint})`);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [advancedInitialGroups, setAdvancedInitialGroups] = useState<
    ConditionGroup[] | undefined
  >();
  // Force the SearchBar into collapsed state after the Advanced Search
  // modal closes. The modal's focus trap restores focus to the input on
  // close, which would otherwise re-expand the bar. Cleared when the
  // user clicks the search icon to expand again.
  const [forceCollapseSearch, setForceCollapseSearch] = useState(false);

  const openAdvancedModal = () => {
    if (search.trim()) {
      // Move search text into advanced conditions as a pre-filled condition
      const searchCondition: ConditionGroup = {
        conditions: [
          {
            field: "path",
            operator: "contains",
            value: search.trim(),
          },
        ],
      };
      const existing = advancedConditions.length > 0 ? advancedConditions : [];
      setAdvancedInitialGroups([...existing, searchCondition]);
      setSearch("");
      setSearchValue("");
    } else {
      setAdvancedInitialGroups(
        advancedConditions.length > 0 ? advancedConditions : undefined,
      );
    }
    setIsAdvancedModalOpen(true);
  };
  // filter hover for styling
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<FilterType[]>(() => {
    const filters: FilterType[] = [FILTER_TYPES.STATUS];
    if (
      selectedPageTypes.length > 0 &&
      !(
        selectedPageTypes.length === 1 &&
        selectedPageTypes[0] === FILTER_VALUES.ALL
      )
    ) {
      filters.push(FILTER_TYPES.TYPE);
    }
    if (
      selectedCustomizations.length > 0 &&
      !(
        selectedCustomizations.length === 1 &&
        selectedCustomizations[0] === FILTER_VALUES.ALL
      )
    ) {
      filters.push(FILTER_TYPES.CUSTOMIZATION);
    }

    return filters;
  });

  const hasSpecificPageType =
    selectedPageTypes.length > 0 &&
    !(
      selectedPageTypes.length === 1 &&
      selectedPageTypes[0] === FILTER_VALUES.ALL
    );

  // statuses and types came from API
  const availableStatuses = useMemo(() => {
    if (!pageStatusesDetail) return [];

    return statusObjects
      .map((s) => s.value)
      .filter((v) => pageStatusesDetail[v] !== undefined);
  }, [pageStatusesDetail]);

  const availableTypes = useMemo(() => {
    if (!pageTypesDetail) return [];

    return Object.keys(pageTypesDetail);
  }, [pageTypesDetail]);

  const filterOptions = useMemo(() => {
    const options: { value: FilterType; label: string }[] = [];

    if (pageStatusesDetail && Object.keys(pageStatusesDetail).length > 0) {
      options.push({
        value: FILTER_TYPES.STATUS,
        label: FILTER_LABELS.STATUS,
      });
    }

    if (pageTypesDetail && Object.keys(pageTypesDetail).length > 0) {
      options.push({
        value: FILTER_TYPES.TYPE,
        label: FILTER_LABELS.TYPE,
      });
    }

    options.push({
      value: FILTER_TYPES.CUSTOMIZATION,
      label: FILTER_LABELS.CUSTOMIZATION,
    });

    return options;
  }, [pageStatusesDetail, pageTypesDetail]);

  const isActivePresetSelected = areEqualAsSets(
    selectedStatuses,
    ACTIVE_STATUSES,
  );
  const isPresetSelected =
    isActivePresetSelected || selectedStatuses.includes(FILTER_VALUES.ALL);
  const isRealStatusSelected = selectedStatuses.length > 0 && !isPresetSelected;

  const isTypePresetSelected = selectedPageTypes.includes(FILTER_VALUES.ALL);
  const isRealTypeSelected =
    selectedPageTypes.length > 0 && !isTypePresetSelected;

  const isCustomizationPresetSelected =
    selectedCustomizations.includes(FILTER_VALUES.ALL) ||
    selectedCustomizations.includes(FILTER_VALUES.NONE);
  const isRealCustomizationSelected =
    selectedCustomizations.length > 0 && !isCustomizationPresetSelected;

  const statusFilterItems = useMemo(() => {
    if (!pageStatusesDetail) return [];

    return [
      { value: FILTER_VALUES.ACTIVE, label: FILTER_LABELS.ACTIVE_STATUS },
      { value: FILTER_VALUES.ALL, label: FILTER_LABELS.ANY_STATUS },
      { value: FILTER_VALUES.DIVIDER, label: "", isDivider: true },
      ...statusObjects
        .filter((s) => pageStatusesDetail[s.value] !== undefined)
        .map((s) => ({
          value: s.value,
          label: s.label,
          count: pageStatusesDetail[s.value],
        })),
    ];
  }, [pageStatusesDetail]);

  const customizationFilterItems = useMemo(
    () => [
      { value: FILTER_VALUES.ALL, label: FILTER_LABELS.ANY_CUSTOMIZATION },
      { value: "divider-top", label: "", isDivider: true },
      ...CUSTOMIZATION_OPTIONS,
      { value: "divider-bottom", label: "", isDivider: true },
      { value: FILTER_VALUES.NONE, label: FILTER_LABELS.NO_CUSTOMIZATION },
    ],
    [],
  );

  const typeFilterItems = useMemo(() => {
    if (!pageTypesDetail) return [];

    const items = Object.entries(pageTypesDetail).map(([key, count]) => ({
      value: key,
      label: formatLabel(key),
      count,
    }));

    return [
      { value: FILTER_VALUES.ALL, label: FILTER_LABELS.ANY_TYPE },
      { value: "divider", label: "", isDivider: true },
      ...items,
    ];
  }, [pageTypesDetail]);

  const ensureValidSelection = useCallback(
    (
      values: string[],
      data: Record<string, number> | undefined,
      setValue: (values: string[]) => void,
    ) => {
      if (
        !data ||
        Object.keys(data).length === 0 ||
        values.includes(FILTER_VALUES.ALL)
      ) {
        return;
      }

      const availableKeys = Object.keys(data);
      const validValues = values.filter(
        (value) => value === FILTER_VALUES.ALL || availableKeys.includes(value),
      );

      if (validValues.length !== values.length) {
        if (validValues.length === 0) {
          setValue([FILTER_VALUES.ALL]);
        } else {
          setValue(validValues);
        }
      }
    },
    [],
  );

  // Ensure selected statuses are still valid after backend data changes
  useEffect(() => {
    ensureValidSelection(
      selectedStatuses,
      pageStatusesDetail,
      setSelectedStatuses,
    );
  }, [pageStatusesDetail, selectedStatuses]);

  // Ensure selected page types are still valid after backend data changes
  useEffect(() => {
    ensureValidSelection(
      selectedPageTypes,
      pageTypesDetail,
      setSelectedPageTypes,
    );

    if (
      pageTypesDetail &&
      Object.keys(pageTypesDetail).length > 0 &&
      hasSpecificPageType &&
      !selectedFilter.includes(FILTER_TYPES.TYPE)
    ) {
      setSelectedFilter((prev) => [...prev, FILTER_TYPES.TYPE]);
    }
  }, [pageTypesDetail, selectedPageTypes, selectedFilter, hasSpecificPageType]);

  // Debounce search input before propagating to parent
  useEffect(() => {
    if (search === searchValue) return;

    const debounceTimeout = setTimeout(() => {
      setSearchValue(search);
    }, 500);

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [search, setSearchValue, searchValue]);

  // Sync pageType from URL query param on first render
  useEffect(() => {
    const pageTypeParam = searchParams.get("pageType");

    if (pageTypeParam) {
      setSelectedPageTypes([pageTypeParam]);
      searchParams.delete("pageType");
      setSelectedStatuses(["completed"]);
      setSearchParams(searchParams, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    setSelectedPageTypes,
    setSelectedStatuses,
  ]);

  // Sync external searchValue changes into local state
  useEffect(() => {
    if (searchValue !== search && searchValue !== undefined) {
      setSearch(searchValue);
    }
  }, [searchValue]);

  // reset pagination to 1st page when filters change
  useEffect(() => {
    if (pagination.currentPage !== 1) {
      setPagination({ ...pagination, currentPage: 1 });
    }
  }, [selectedStatuses, selectedPageTypes, selectedCustomizations]);

  const handleStatusSelect = (value: string) => {
    if (value === FILTER_VALUES.ALL) {
      setSelectedStatuses([FILTER_VALUES.ALL]);

      return;
    }

    if (value === FILTER_VALUES.ACTIVE) {
      setSelectedStatuses([...ACTIVE_STATUSES]);

      return;
    }

    setSelectedStatuses((prev) => {
      // a Preset was selected - uncheck all but a new value
      const wasPresetSelected =
        prev.includes(FILTER_VALUES.ALL) ||
        areEqualAsSets(prev, ACTIVE_STATUSES);

      if (wasPresetSelected) {
        return [value];
      }

      const next = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];

      // if none selected → set Any
      if (!next.length) {
        return [FILTER_VALUES.ALL];
      }

      // if all available statuses selected → Any
      const isAllAvailableSelected = areEqualAsSets(next, availableStatuses);

      if (isAllAvailableSelected) {
        return [FILTER_VALUES.ALL];
      }

      return next;
    });
  };

  const handleTypeSelect = (value: string) => {
    if (value === FILTER_VALUES.ALL) {
      setSelectedPageTypes([FILTER_VALUES.ALL]);

      return;
    }

    setSelectedPageTypes((prev) => {
      const wasAllSelected = prev.includes(FILTER_VALUES.ALL);

      // if "all" was selected, replace it with the new value
      if (wasAllSelected) {
        return [value];
      }

      // toggle value
      const next = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];

      // set "all" when none selected
      if (!next.length) {
        return [FILTER_VALUES.ALL];
      }

      const isAllAvailableSelected = areEqualAsSets(next, availableTypes);
      if (isAllAvailableSelected) {
        return [FILTER_VALUES.ALL];
      }

      return next;
    });
  };

  const handleCustomizationSelect = (value: string) => {
    if (value === FILTER_VALUES.ALL) {
      setSelectedCustomizations([FILTER_VALUES.ALL]);

      return;
    }

    if (value === FILTER_VALUES.NONE) {
      setSelectedCustomizations([FILTER_VALUES.NONE]);

      return;
    }

    setSelectedCustomizations((prev) => {
      const wasAllSelected = prev.includes(FILTER_VALUES.ALL);
      const wasNoneSelected = prev.includes(FILTER_VALUES.NONE);

      if (wasAllSelected || wasNoneSelected) {
        return [value];
      }

      const next = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];

      if (!next.length) {
        return [FILTER_VALUES.ALL];
      }

      if (areEqualAsSets(next, AVAILABLE_CUSTOMIZATIONS)) {
        return [FILTER_VALUES.ALL];
      }

      return next;
    });
  };

  const toggleTableView = () => {
    setIsCompactView(!isCompactView);
  };

  const showStatusFilter =
    selectedFilter.includes(FILTER_TYPES.STATUS) &&
    pageStatusesDetail &&
    (Object.keys(pageStatusesDetail).length > 0 || isLoading);

  const showTypeFilter =
    selectedFilter.includes(FILTER_TYPES.TYPE) &&
    pageTypesDetail &&
    (Object.keys(pageTypesDetail).length > 0 || isLoading);

  const showCustomizationFilter = selectedFilter.includes(
    FILTER_TYPES.CUSTOMIZATION,
  );

  const statusItemsWithClass = statusFilterItems.map((item) =>
    getItemClass({
      item,
      isPresetSelected,
      isRealSelected: isRealStatusSelected,
      isPresetItem:
        item.value === FILTER_VALUES.ACTIVE || item.value === FILTER_VALUES.ALL,
      styles: selectFilterStyles,
    }),
  );
  const typeItemsWithClass = typeFilterItems.map((item) =>
    getItemClass({
      item,
      isPresetSelected: isTypePresetSelected,
      isRealSelected: isRealTypeSelected,
      isPresetItem: item.value === FILTER_VALUES.ALL,
      styles: selectFilterStyles,
    }),
  );
  const customizationItemsWithClass = customizationFilterItems.map((item) =>
    getItemClass({
      item,
      isPresetSelected: isCustomizationPresetSelected,
      isRealSelected: isRealCustomizationSelected,
      isPresetItem:
        item.value === FILTER_VALUES.ALL || item.value === FILTER_VALUES.NONE,
      styles: selectFilterStyles,
    }),
  );

  const statusTitle = getStatusFilterTitle(selectedStatuses);
  const typeTitle = getTypeFilterTitle(selectedPageTypes);
  const customizationTitle = getCustomizationFilterTitle(
    selectedCustomizations,
  );

  return (
    <>
      <div className={styles.filterWrapper}>
        <div className={styles.filterWrapperFilters}>
          <div className={styles.filterWrapperFiltersLeft}>
            <div className={styles.searchWrapper}>
              <SearchBar
                isShort={true}
                onChange={setSearch}
                value={search}
                forceCollapsed={forceCollapseSearch}
                onExpandRequest={() => setForceCollapseSearch(false)}
                placeholder="Search Pages"
                endAdornment={
                  <button
                    type="button"
                    className={clsx(
                      styles.advancedBtn,
                      advancedConditions.length > 0 && styles.advancedBtnActive,
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openAdvancedModal();
                    }}
                    data-tooltip-id="page-filters-tooltip"
                    data-tooltip-content="Advanced Search"
                  >
                    <Icon
                      iconName="AdvancedSearchIcon"
                      width="1rem"
                      height="1rem"
                    />
                  </button>
                }
              />
              {advancedConditions.length > 0 && !search && (
                <button
                  type="button"
                  className={styles.searchBadge}
                  onClick={openAdvancedModal}
                >
                  {advancedConditions.length}
                </button>
              )}
            </div>
            <SelectFilterButton
              filterOptions={filterOptions || []}
              onSelect={(selected: string[]) => {
                const filters = selected as FilterType[];
                setSelectedFilter(filters);
                if (!filters.includes(FILTER_TYPES.STATUS)) {
                  setSelectedStatuses([...DEFAULT_STATUSES]);
                }
                if (!filters.includes(FILTER_TYPES.TYPE)) {
                  setSelectedPageTypes([FILTER_VALUES.ALL]);
                }
                if (!filters.includes(FILTER_TYPES.CUSTOMIZATION)) {
                  setSelectedCustomizations([FILTER_VALUES.ALL]);
                }
              }}
              value={selectedFilter}
            />
            {showStatusFilter && (
              <SelectFilter
                items={statusItemsWithClass}
                filterTitle={statusTitle}
                onSelect={handleStatusSelect}
                getVisualState={(value) =>
                  getStatusCheckboxVisualState(
                    value,
                    selectedStatuses,
                    hoveredValue,
                  )
                }
                setHoveredValue={setHoveredValue}
              />
            )}

            {showTypeFilter && (
              <SelectFilter
                items={typeItemsWithClass}
                filterTitle={typeTitle}
                onSelect={handleTypeSelect}
                getVisualState={(value) =>
                  getTypeCheckboxVisualState(
                    value,
                    selectedPageTypes,
                    hoveredValue,
                  )
                }
                setHoveredValue={setHoveredValue}
              />
            )}

            {showCustomizationFilter && (
              <SelectFilter
                items={customizationItemsWithClass}
                filterTitle={customizationTitle}
                onSelect={handleCustomizationSelect}
                getVisualState={(value) =>
                  getCustomizationCheckboxVisualState(
                    value,
                    selectedCustomizations,
                    hoveredValue,
                  )
                }
                setHoveredValue={setHoveredValue}
              />
            )}
          </div>
          <div className={styles.filterWrapperFiltersRight}>
            <Button
              variant="outlined"
              onClick={toggleTableView}
              className={styles.toggleTableViewBtn}
              data-tooltip-id="page-filters-tooltip"
              data-tooltip-content={
                isCompactView ? "Expand Rows" : "Compact Rows"
              }
            >
              <Icon
                iconName={isCompactView ? "ExpandIcon" : "ColapseIcon"}
                width="1rem"
                height="1rem"
              />
            </Button>

            {combobox && (
              <RecrawlCombobox
                selectedIds={selectedIds}
                selectedIdsToRecrawl={selectedIdsToRecrawl}
                selectedIdsToIgnore={selectedIdsToIgnore}
                hasMixedIgnoreStatuses={hasMixedIgnoreStatuses}
                canSoftCrawl={canSoftCrawl}
                canForceRecrawl={canForceRecrawl}
                canAbortCrawl={canAbortCrawl}
                recrawlPagesBulk={recrawlPagesBulk}
                openBulkIgnoreModal={openBulkIgnoreModal}
                openCrawlModal={openCrawlModal}
                setSelectedIds={setSelectedIds}
                combobox={combobox}
                isOpened={isOpened}
                setIsOpened={setIsOpened}
              />
            )}
            <Button
              className={styles.generateBtn}
              onClick={() => onGenerateClick(selectedIds)}
              disabled={!canGenerate}
              {...(isCompact && {
                "data-tooltip-id": "page-filters-tooltip",
                "data-tooltip-content": "Generate AI Content",
              })}
            >
              <Icon iconName="StarIcon" width="1rem" height="1rem" />
              <span className={styles.btnLabel}>Generate</span>
            </Button>
            <Button
              onClick={openAddUrlModal}
              disabled={!canAddUrl}
              {...(isCompact && {
                "data-tooltip-id": "page-filters-tooltip",
                "data-tooltip-content": "Add URL",
              })}
            >
              <Icon iconName="PlusIcon" width="1rem" height="1rem" />
              <span className={styles.btnLabel}>Add URL</span>
            </Button>
          </div>
        </div>
      </div>
      <AdvancedConditionsModal
        isOpen={isAdvancedModalOpen}
        onClose={() => {
          setIsAdvancedModalOpen(false);
          setForceCollapseSearch(true);
        }}
        onApply={onAdvancedConditionsChange}
        initialGroups={advancedInitialGroups}
      />
      <Tooltip
        id="page-filters-tooltip"
        openEvents={{ mouseenter: true, focus: false }}
        closeEvents={{ mouseleave: true, click: true, blur: true }}
        style={{
          borderRadius: "var(--border-radius)",
          font: "var(--ui-text-12-medium)",
        }}
      />
    </>
  );
};

export default PageFilters;
